import config from '@/config'
import { Platform } from '@/helper/const'
import {
  LiveState,
  LiveStreamType,
  saveObjectToLocalStorage,
} from '@/helper/go.live.helper'
import { useGlobalStore } from '@/store/app'
import { UploadMediaResponse } from '@/types'
import { Capacitor } from '@capacitor/core'
import { v4 as uuidv4 } from 'uuid'
import { requests } from './HTTPService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('groupId')
class MediaServiceCls {
  constructor() {}

  private get groupId(): string {
    const appStore = useGlobalStore()
    return appStore.groupId
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/medias`
  }

  async generateSignedUrl(data: any): Promise<any> {
    return http
      .post(`${this.endpoint}/signed-url`, data, {
        params: { groupId: this.groupId },
      })
      .then(res => res.data)
  }

  async generateSettingsSignedUrl(data: any): Promise<any> {
    return http.post(`${this.endpoint}/signed-url`, data).then(res => res.data)
  }

  async uploadFile(payload: any): Promise<{
    uploadPromise: Promise<UploadMediaResponse>
    cancelUpload: Function
  }> {
    const name: string = payload.filename || uuidv4()
    const folder: string = payload.folder || 'other'

    const { signedUrl, url } = await this.generateSignedUrl({
      name,
      folder,
      type: payload.type,
    })

    const blobedFile = new File([payload.file], name)
    const xhr = new XMLHttpRequest()

    const uploadPromise: Promise<UploadMediaResponse> = new Promise(function (
      resolve,
      reject
    ) {
      const formData = new FormData()
      formData.append('file', blobedFile)
      xhr.open('PUT', signedUrl, true)
      xhr.onload = () => {
        const status = xhr.status
        if (status === 200) {
          resolve({
            id: payload.id,
            url: url,
            name: name,
            type: payload.type,
            size: payload.size,
            status: 'completed',
          })
        } else {
          reject('Something went wrong')
        }
      }
      xhr.onerror = () => {
        reject('Something went wrong')
      }
      xhr.setRequestHeader('Content-Type', payload.type)
      if (payload.progressUpdate) {
        if (
          Capacitor.getPlatform() === Platform.IOS ||
          Capacitor.getPlatform() === Platform.Android
        ) {
          // Fake progress for iOS and Android
          let progress = 0
          const progressInterval = setInterval(() => {
            progress += 20
            if (progress > 90) {
              progress = 90
              clearInterval(progressInterval)
            }
            payload.progressUpdate(progress)
          }, 500)
        } else {
          xhr.upload.onprogress = function (event) {
            if (event.lengthComputable) {
              const percentComplete = Number(
                ((event.loaded / event.total) * 100).toFixed()
              )
              payload.progressUpdate(percentComplete)
            }
          }
        }
      }
      xhr.onloadend = function () {
        if (payload.progressUpdate) {
          // Set progress to 100 when upload is complete
          payload.progressUpdate(100)
        }
      }
      xhr.send(blobedFile)
    })

    const cancelUpload = () => {
      xhr.abort()
    }

    return { uploadPromise, cancelUpload }
  }

  async getMediaById(_id: string): Promise<any> {
    // Implementation of getMediaById method
  }

  async deleteMediaById(_id: string): Promise<void> {
    // Implementation of deleteMediaById method
  }

  async createLicenseForAsset(
    groupId: string,
    payload: {
      type: LiveStreamType
      hostDetails?: {
        id: string
        name: string
        role: 'host' | 'attendee'
        protocol: 'rtmp' | 'webrtc'
      }
    }
  ): Promise<{
    licenseId: string
    sourceEntityId: string
    serverUrl: string
    token: string
  }> {
    const response = await http.post(
      `${this.endpoint}/groups/${groupId}/license`,
      payload
    )

    const data = {
      licenseId: response.data.licenseId,
      sourceEntityId: response.data.sourceEntityId,
      ...response.data.stream,
    }
    saveObjectToLocalStorage('streamingDetails', data)
    return data
  }
  async getStreamToken(
    licenseId: string,
    payload: {
      id: string
      name: string
      role: 'host' | 'attendee'
      protocol: 'rtmp' | 'webrtc'
      avatar?: string
    }
  ): Promise<{
    serverUrl: string
    token: string
  }> {
    const response = await http.post(
      `${this.endpoint}/licenses/${licenseId}/stream-token`,
      payload
    )
    return response.data
  }
  async getStreamLiveState(licenseId: string): Promise<{
    state: LiveState
    recordingEnabled: boolean
    peakAttendance?: number
    recordingState?: 'completed'
  }> {
    const response = await http.get(
      `${this.endpoint}/licenses/${licenseId}/live-state`
    )
    return response.data
  }

  async getDownloadSignedUrl(licenseId: string): Promise<{
    signedUrl: string
  }> {
    const response = await http.get(
      `${this.endpoint}/licenses/${licenseId}/signed-url/download`
    )
    return response.data
  }
}

// Export singleton instance
export const MediaService = new MediaServiceCls()

// Export type for dependency injection
export type IMediaService = MediaServiceCls

// Export commonly used methods directly
export const {
  generateSignedUrl,
  generateSettingsSignedUrl,
  getMediaById,
  deleteMediaById,
  createLicenseForAsset: getStreamingDetails,
} = MediaService

// Composable hook for easier usage
export function useMedia() {
  const mediaService = MediaService

  return {
    generateSignedUrl: mediaService.generateSignedUrl.bind(mediaService),
    generateSettingsSignedUrl:
      mediaService.generateSettingsSignedUrl.bind(mediaService),
    getMediaById: mediaService.getMediaById.bind(mediaService),
    deleteMediaById: mediaService.deleteMediaById.bind(mediaService),
  }
}
